import { HTMLAttributes } from 'react';
import { AvatarStyleVariants, avatarStyles } from './variants';
import { twMerge } from 'tailwind-merge';
import { Typography } from '../typography/typography';
import { match } from 'ts-pattern';
import { TypographyVariants } from '../typography/variants';

type Props = {
  initials: string;
  src?: string;
  alt?: string;
  className?: string;
} & Omit<HTMLAttributes<HTMLDivElement>, 'className'> &
  AvatarStyleVariants;

export function Avatar(props: Props) {
  const { className, variant, size, src, alt, initials, children, ...rest } =
    props;

  const showInitials = !src;

  return (
    <div
      className={twMerge(
        avatarStyles({ variant, size }),
        showInitials && 'flex items-center justify-center',
        className,
      )}
      role="img"
      aria-label={alt} // Provide accessible text for the avatar
      {...rest}
    >
      {showInitials ? (
        <Typography
          variant={mapSizeToTypographySize(size)}
          className="text-primaryMain"
        >
          {initials}
        </Typography>
      ) : (
        <img
          src={src}
          alt="" // Decorative image: `alt` is empty because `aria-label` handles description
        />
      )}
    </div>
  );
}

function mapSizeToTypographySize(
  size: AvatarStyleVariants['size'],
): TypographyVariants['variant'] {
  return match(size)
    .returnType<TypographyVariants['variant']>()
    .with(16, () => 'body-xxs')
    .with(24, () => 'body-xs')
    .with(32, () => 'body-s')
    .with(40, () => 'body-m')
    .with(48, () => 'display6')
    .otherwise(() => 'body-m');
}
